import React from 'react';
import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

const C = { void:'#030A3C', void2:'#060D4A', void3:'#0A1260', cyan:'#00FFFF', cyanDim:'rgba(0,255,255,0.10)', cyanBorder:'rgba(0,255,255,0.15)', magma:'#FF5722', magmaDim:'rgba(255,87,34,0.12)', magmaBorder:'rgba(255,87,34,0.25)', white:'#F8F9FA', muted:'rgba(248,249,250,0.72)', muted2:'rgba(248,249,250,0.72)', border:'rgba(0,255,255,0.08)', border2:'rgba(0,255,255,0.15)', green:'#2A8B7A', greenDim:'rgba(42,139,122,0.12)' };

const TOOLS = [
  {
    name: 'upload_research_file',
    tag: 'Ingestion',
    title: 'Upload any research artifact',
    body: 'Drop in audio, video, or transcript files. SenseCritiq SHA-256 deduplicates - uploading the same file twice costs nothing. Returns a session ID and estimated processing time.',
    params: 'file_path · session_name · project_id · tags[]',
    returns: 'session_id · status · estimated_processing_time',
  },
  {
    name: 'get_session_status',
    tag: 'Monitoring',
    title: 'Track processing progress',
    body: 'Poll a session\'s status in real time. States: queued → processing → ready → failed. Includes a progress percentage so you know where in the pipeline your file sits.',
    params: 'session_id',
    returns: 'status · progress_pct',
  },
  {
    name: 'get_synthesis',
    tag: 'Insights',
    title: 'Retrieve themes and findings',
    body: 'Pull the full synthesis for a session: themes, key findings, quote count, and participant count. Every finding cites a source. Every claim has evidence.',
    params: 'session_id',
    returns: 'themes[] · key_findings[] · quote_count · participant_count',
  },
  {
    name: 'get_quotes',
    tag: 'Evidence',
    title: 'Browse and filter quotes',
    body: 'Retrieve all quotes for a session, or filter by theme ID. Each quote includes the verbatim text, speaker label, timestamp, and theme label. The raw material for your presentations.',
    params: 'session_id · theme_id (optional)',
    returns: 'quotes[] with text, speaker, timestamp, theme_label',
  },
  {
    name: 'search_research',
    tag: 'Memory',
    title: 'Semantic search across all sessions',
    body: 'Ask a natural language question and get relevant quotes from your entire research history - across projects and dates. Powered by pgvector embeddings. The longer you use SenseCritiq, the more powerful this gets.',
    params: 'query · project_id · date_from · date_to',
    returns: 'results[] with quote, session_name, date, relevance_score, synthesis_summary',
    highlight: true,
  },
  {
    name: 'list_sessions',
    tag: 'Navigation',
    title: 'Browse your session library',
    body: 'List sessions with optional project filter and pagination. Returns key metadata so you can surface the right session before diving into synthesis.',
    params: 'project_id · limit · offset',
    returns: 'sessions[] with id, name, date, status',
  },
  {
    name: 'generate_report',
    tag: 'Output',
    title: 'Generate reports on demand',
    body: 'Export a session as a Markdown or PDF report with a pre-signed download URL (expires in 24 hours). Markdown reports pipe cleanly into Notion, Confluence, or your docs system.',
    params: 'session_id · format (markdown | pdf | notion)',
    returns: 'download_url · expires_at',
  },
];

const CAPABILITIES = [
  { icon:'⟡', title:'Source-cited findings', body:'Post-processing validates every finding has a verbatim quote. No unsourced claims reach your output.' },
  { icon:'◈', title:'Speaker diarisation', body:'AssemblyAI labels each speaker automatically. Quotes know who said them.' },
  { icon:'⌖', title:'Research memory', body:'pgvector semantic search across your organisation\'s entire session history. Context compounds over time.' },
  { icon:'⬡', title:'Deduplication', body:'SHA-256 file hashing prevents double-billing if you upload the same recording twice.' },
  { icon:'◉', title:'Structured JSON', body:'All outputs conform to a predictable schema. Build downstream automations on top.' },
  { icon:'◫', title:'Async processing', body:'Upload and forget. Modal workers handle transcription and synthesis in the background. No blocking.' },
];

export default function Features() {
  return (
    <div style={s.root}>
      <MarketingNav />

      {/* Hero */}
      <div style={s.hero} className="scq-hero">
        <div style={s.heroBg} />
        <div style={s.eyebrow}>Everything included</div>
        <h1 style={s.h1}>Seven tools. One research memory.</h1>
        <p style={s.heroSub}>All features are available on every plan, including Free. No paywalled tool calls.</p>
      </div>

      {/* MCP tool catalogue */}
      <div style={s.toolsWrap}>
        <div style={s.toolsInner}>
          <div style={s.sectionEyebrow}>MCP tool catalogue</div>
          <h2 style={s.sectionH2}>Everything you can do from Claude.</h2>
          <div style={s.toolsGrid} className="scq-grid-3">
            {TOOLS.map(tool => (
              <div key={tool.name} className="scq-card" style={{...s.toolCard, ...(tool.highlight ? s.toolCardHighlight : {})}}>
                {tool.highlight && <div style={s.highlightBadge}>Most powerful</div>}
                <div style={s.toolTag}>{tool.tag}</div>
                <div style={s.toolName}>{tool.name}()</div>
                <h3 style={s.toolTitle}>{tool.title}</h3>
                <p style={s.toolBody}>{tool.body}</p>
                <div style={s.toolMeta}>
                  <div style={s.toolMetaRow} className="scq-tool-row">
                    <span style={s.toolMetaLabel} className="scq-tool-cell">Params</span>
                    <span style={s.toolMetaValue} className="scq-tool-cell">{tool.params}</span>
                  </div>
                  <div style={s.toolMetaRow} className="scq-tool-row">
                    <span style={s.toolMetaLabel} className="scq-tool-cell">Returns</span>
                    <span style={s.toolMetaValue} className="scq-tool-cell">{tool.returns}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Capabilities grid */}
      <div style={s.capsWrap}>
        <div style={s.capsInner}>
          <div style={s.sectionEyebrow}>Under the hood</div>
          <h2 style={s.sectionH2}>Built for research trust.</h2>
          <div style={s.capsGrid} className="scq-grid-3">
            {CAPABILITIES.map(cap => (
              <div key={cap.title} className="scq-card" style={s.capCard}>
                <div style={s.capIcon}>{cap.icon}</div>
                <h3 style={s.capTitle}>{cap.title}</h3>
                <p style={s.capBody}>{cap.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Output formats */}
      <div style={s.formatsWrap}>
        <div style={s.formatsInner} className="scq-grid-2">
          <div style={s.formatsLeft}>
            <div style={s.sectionEyebrow}>Report formats</div>
            <h2 style={s.formatsH2}>Outputs that fit your workflow.</h2>
            <div style={s.formatsList}>
              {[
                { fmt:'Markdown', desc:'Pipe directly to Notion, Confluence, or GitHub Discussions. Headers, quotes, and findings structured for human review.' },
                { fmt:'PDF', desc:'Download-ready reports for stakeholder presentations. Themes, findings, and supporting quotes, paginated.' },
                { fmt:'Notion', desc:'Direct page creation in your Notion workspace via OAuth. Full formatting preserved.' },
              ].map(({ fmt, desc }) => (
                <div key={fmt} style={s.formatItem}>
                  <div style={s.formatFmt}>{fmt}</div>
                  <p style={s.formatDesc}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div style={s.formatsRight}>
            <div className="scq-card" style={s.codeCard}>
              <div style={s.codeDots}>
                <span style={{...s.codeDot, background:'#FF5F57'}} />
                <span style={{...s.codeDot, background:'#FFBD2E'}} />
                <span style={{...s.codeDot, background:'#28CA41'}} />
              </div>
              <pre style={s.code}>{`# Onboarding Exit Interviews - March 2026

## Theme: Invitation friction
_3 participants · 7 supporting quotes_

**Finding:** The invite flow breaks when the
invitee already has an account.

> "I kept hitting that wall every time
>  I tried to invite someone."
> - Speaker B · 2:22 · sess_abc123

**Finding:** Error messages don't explain
what went wrong or how to fix it.

> "It just said 'something went wrong'.
>  I had no idea what to do next."
> - Speaker A · 8:45 · sess_abc123`}</pre>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={s.bottomCta}>
        <div style={s.bottomInner} className="scq-bottom-inner">
          <h2 style={s.bottomH2}>All features. Free to start.</h2>
          <p style={s.bottomSub}>Two sessions/month on the Free plan. No credit card required.</p>
          <div style={s.bottomBtns}>
            <Link to="/sign-up" style={s.ctaPrimary}>Get started free</Link>
            <Link to="/pricing" style={s.ctaGhost}>See pricing →</Link>
          </div>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}

const s = {
  root: { background:C.void, minHeight:'100vh', fontFamily:"'Inter',sans-serif", color:C.white },
  hero: { textAlign:'center', padding:'100px 24px 80px', position:'relative', overflow:'hidden' },
  heroBg: { position:'absolute', inset:0, background:'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,255,255,0.07) 0%, transparent 65%)', pointerEvents:'none' },
  eyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, position:'relative' },
  h1: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(36px,5vw,64px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.05, marginBottom:20, position:'relative' },
  heroSub: { fontSize:18, color:C.muted, maxWidth:500, margin:'0 auto', lineHeight:1.65, position:'relative' },

  toolsWrap: { padding:'80px 24px 80px' },
  toolsInner: { maxWidth:1100, margin:'0 auto' },
  sectionEyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, textAlign:'center' },
  sectionH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(26px,3.5vw,44px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:48, textAlign:'center' },
  toolsGrid: { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 },
  toolCard: { background:'linear-gradient(135deg, rgba(6,13,74,0.65) 0%, rgba(10,18,96,0.72) 100%)', border:`1px solid ${C.border}`, borderRadius:18, padding:'28px 24px', position:'relative', boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  toolCardHighlight: { border:`1px solid ${C.cyanBorder}`, background:'linear-gradient(135deg, rgba(10,18,96,0.70) 0%, rgba(14,22,110,0.75) 100%)', gridColumn:'span 1' },
  highlightBadge: { position:'absolute', top:-1, right:16, fontSize:10, fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', color:C.void, background:C.cyan, padding:'4px 10px', borderRadius:'0 0 8px 8px' },
  toolTag: { fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:C.cyan, marginBottom:8 },
  toolName: { fontFamily:"'JetBrains Mono','Fira Code',monospace", fontSize:12, color:C.muted2, marginBottom:10 },
  toolTitle: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:17, fontWeight:700, letterSpacing:'-0.01em', lineHeight:1.3, marginBottom:10 },
  toolBody: { fontSize:13, color:C.muted, lineHeight:1.65, marginBottom:16 },
  toolMeta: { borderTop:`1px solid ${C.border}`, paddingTop:14, display:'flex', flexDirection:'column', gap:6 },
  toolMetaRow: { display:'flex', gap:8, alignItems:'flex-start' },
  toolMetaLabel: { fontSize:10, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:C.muted2, flexShrink:0, paddingTop:1, minWidth:48 },
  toolMetaValue: { fontSize:11, color:C.muted, fontFamily:"'JetBrains Mono','Fira Code',monospace", lineHeight:1.5 },

  capsWrap: { background:C.void2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:'80px 24px' },
  capsInner: { maxWidth:1100, margin:'0 auto' },
  capsGrid: { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 },
  capCard: { padding:'24px', borderRadius:14, background:'linear-gradient(135deg, rgba(10,18,96,0.70) 0%, rgba(14,22,110,0.75) 100%)', border:`1px solid ${C.border}`, boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  capIcon: { fontSize:20, color:C.cyan, marginBottom:12 },
  capTitle: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:16, fontWeight:700, letterSpacing:'-0.01em', marginBottom:8 },
  capBody: { fontSize:13, color:C.muted, lineHeight:1.65 },

  formatsWrap: { padding:'80px 24px' },
  formatsInner: { maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:80, alignItems:'start' },
  formatsLeft: {},
  formatsH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(24px,3vw,38px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:32 },
  formatsList: { display:'flex', flexDirection:'column', gap:4 },
  formatItem: { borderBottom:`1px solid ${C.border}`, paddingBottom:20, marginBottom:16 },
  formatFmt: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:16, fontWeight:700, color:C.cyan, marginBottom:8 },
  formatDesc: { fontSize:14, color:C.muted, lineHeight:1.7 },
  formatsRight: {},
  codeCard: { background:'rgba(2,8,24,0.90)', backdropFilter:'blur(14px)', WebkitBackdropFilter:'blur(14px)', border:`1px solid rgba(0,255,255,0.12)`, borderRadius:14, padding:'18px 20px', boxShadow:'0 4px 24px rgba(0,0,0,0.60), inset 0 1px 0 rgba(0,255,255,0.08), inset 0 0 0 1px rgba(0,255,255,0.03)' },
  codeDots: { display:'flex', gap:6, marginBottom:14 },
  codeDot: { width:9, height:9, borderRadius:'50%' },
  code: { margin:0, fontSize:12.5, color:'rgba(248,249,250,0.72)', lineHeight:1.8, overflowX:'auto', fontFamily:"'JetBrains Mono','Fira Code',monospace" },

  bottomCta: { background:C.void2, borderTop:`1px solid ${C.border}`, padding:'80px 24px', textAlign:'center' },
  bottomInner: { maxWidth:560, margin:'0 auto' },
  bottomH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(26px,3.5vw,40px)', fontWeight:700, letterSpacing:'-0.03em', marginBottom:14 },
  bottomSub: { fontSize:16, color:C.muted, marginBottom:32 },
  bottomBtns: { display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap' },
  ctaPrimary: { display:'inline-block', padding:'14px 30px', borderRadius:10, background:C.magma, color:'#fff', fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:15, textDecoration:'none' },
  ctaGhost: { display:'inline-block', padding:'14px 26px', borderRadius:10, background:'rgba(255,255,255,0.05)', color:C.muted, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:600, fontSize:15, textDecoration:'none', border:`1px solid ${C.border}` },
};
